import { Subject, type Subscription } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import Skeleton from '@material-ui/lab/Skeleton'
import useIsMountedRef from 'use-is-mounted-ref'
import React, {
    ChangeEvent,
    FC,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import clsx from 'clsx'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import FilterListIcon from '@material-ui/icons/FilterList'
import CategoryIcon from '@material-ui/icons/Category'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { GenericAdminFilterLanguage } from '../../../../../i18n'
import { SelectFilter, SelectFilterOption } from './SelectFilter'
import { CmeIcon } from '../../Atoms/Icons/Cme'
import { TelSysIcon } from '../../Atoms/Icons/TelSys'
import { PartitionIcon } from '../../Atoms/Icons/Partition'
import { DEFAULT_CME_ID } from '../../../../React/Common/Constants/ServerValues'
import {
    getCMEsAsSelectOptions,
    getPartitionsForTelServer,
    getTelServersAsSelectOptionsWithCXIFilter
} from '../../../../React/Common/API/ItemsGetters'
import { SelectOption, SelectOptions } from '../../../../React/Common/Components/Selects/Types'
import { ALL_VALUE, ANY_VALUE_STRING_NUMERIC } from '../../../../React/Common/Constants/DefaultValues'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { displayError } from '../../../../React/Common/Utils/Messages'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import TextInput from '../../../../React/Common/Components/TextInput'
import IconButton, { IconButtonType } from '../../../../React/Common/Components/IconButton'
import { IconName } from '../../../../React/Common/Components/Icon'
import type { CME } from '../../../../React/Common/Types/Types'
import { $error } from '../../../../React/Common/Utils/ConsoleUtils'
import { CRMTypes, useCRMTypes } from '../../../Hooks/useCRMTypes'
import { CrmIcon } from '../../Atoms/Icons/Crm'
import { CRMTypesResponse } from '../../../../NonReact/common/Types/Types'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { PermissionsUtil } from '../../../../React/Common/Utils/PermissionsUtil'
import { AdminListUrlBootstrap } from '../../../Utils/adminListUrlParams'
import { TextFilterNames } from './AdminFiltersTextFilterNames'

export { TextFilterNames } from './AdminFiltersTextFilterNames'

export interface AdminFiltersLocaleText {
    cmeLabel: string
    telephonyServerLabel?: string
    partitionLabel?: string
    crmTypeLabel?: string
    includeChildCmeLabel?: string
    searchLabel?: string
    scopeLabel?: string
    codeTypeLabel?: string
    userTypeLabel?: string
}

export enum SelectFilterNames {
    CME = 'cme',
    TELEPHONY_SERVER = 'telephonyServer',
    PARTITION = 'partition',
    CRM_TYPE = 'crmType',
    LICENCE_TYPE = 'licenceType',
    SCOPE = 'scope',
    CODE_TYPE = 'codeType',
    USER_TYPE = 'userType'
}

export enum SwitchFilterNames {
    INCLUDE_CME_CHILDREN = 'includeCmeChildren',
    INCLUDE_REFRESH = 'includeRefresh'
}

export type AdminFilterNames =
    | SelectFilterNames.CME
    | SelectFilterNames.CRM_TYPE
    | SelectFilterNames.PARTITION
    | SelectFilterNames.TELEPHONY_SERVER
    | SelectFilterNames.SCOPE
    | SelectFilterNames.CODE_TYPE
    | SelectFilterNames.LICENCE_TYPE
    | SelectFilterNames.USER_TYPE
    | SwitchFilterNames.INCLUDE_CME_CHILDREN
    | SwitchFilterNames.INCLUDE_REFRESH
    | TextFilterNames.SEARCH

export interface AdminFilterValues {
    cme: Nullable<SelectFilterOption>
    telephonyServer: Nullable<SelectFilterOption>
    partition: Nullable<SelectFilterOption>
    crmType: Nullable<SelectFilterOption>
    licenceType?: Nullable<SelectFilterOption>
    scope: Nullable<SelectFilterOption>
    codeType: Nullable<SelectFilterOption>
    userType?: Nullable<SelectFilterOption>
    includeCmeChildren: Nullable<boolean>
    search: Definable<string>
    noTelephonyServer?: boolean
    /** Audit trail: local {@code YYYY-MM-DD HH:mm:ss} upper bound forwarded as list API {@code until} (omit {@code since}). */
    changeLogBefore?: Definable<string>
    /** Audit trail: selected action names forwarded as API {@code include} (comma-separated). */
    changeLogActions?: readonly string[]
    /** Audit trail: selected table / entity type names forwarded as API {@code type} (comma-separated). */
    changeLogTypes?: readonly string[]
    /** Audit trail: maps to API {@code deNull} when true. */
    changeLogSuppressNulls?: Nullable<boolean>
    cxiTelSysOnly?: boolean
}

export interface AdminFilterSingleValues {
    cme?: string | number
    telephonyServer?: string | number
    partition?: string | number
    device?: string
}

type AdminFiltersSessionState = {
    cmeId?: string
    telephonyServer?: string
    partition?: string
}

type OptionsToDisplay = {
    [key in AdminFilterNames]?: boolean
}

/**
 * Context passed to {@link AdminFiltersProps.additionalFilters} so slot content can mirror loading/disabled state.
 */
export type AdminFiltersSlotRenderArgs = {
    isLoading: boolean
    filterValues: AdminFilterValues
    onSelectFilterChange: (filter: SelectFilterNames, option: Nullable<SelectFilterOption>) => void
    /**
     * Audit trail: set selected REST {@code include} action names. Passed for every page; list-specific
     * {@code additionalFilters} content may ignore.
     */
    onChangeLogActionsChange?: (nextActions: readonly string[]) => void
    /** Audit trail: set selected REST {@code type} entity names. */
    onChangeLogTypesChange?: (nextTypes: readonly string[]) => void
    /** Audit trail: toggle API {@code deNull} via {@link AdminFilterValues.changeLogSuppressNulls}. */
    onChangeLogSuppressNullsChange?: (suppressNulls: boolean) => void
    /**
     * Updates a text-backed filter field (for example {@link TextFilterNames.SEARCH} or {@link TextFilterNames.CHANGE_LOG_BEFORE})
     * from {@code additionalFilters} content.
     */
    onTextFilterChange?: (filter: TextFilterNames, value: string) => void
}

type SelectFilterPlaceholderTooltipEntry = boolean | { readonly show: boolean; readonly title: string }

interface AdminFiltersProps {
    optionsToDisplay: OptionsToDisplay
    localeText: AdminFiltersLocaleText
    onChange: (values: AdminFilterValues) => void
    onRefresh?: () => void
    cmeFiltering?: [string[], (cme: CME) => boolean]
    alignGrid?: boolean
    labelAlign?: 'top' | 'left'
    allowCXIOnly?: boolean
    /**
     * Optional extra filter controls rendered after the partition selector and before the include-child-CME switch.
     * Keeps list-specific filters (CRM type, TelSys column filters, etc.) out of this shared component.
     */
    additionalFilters?: (args: AdminFiltersSlotRenderArgs) => React.ReactNode
    /**
     * Extra skeleton placeholders to show while {@link AdminFiltersSlotRenderArgs.isLoading} is true.
     */
    additionalFilterSkeletonCount?: number
    /** Options for scope filter (e.g. All, ServiceWide, SystemWide, Detached). */
    scopeOptions?: SelectFilterOption[]
    /** Options for code type filter (e.g. All, Account, ACD Not Available). */
    codeTypeOptions?: SelectFilterOption[]
    /** Options for application user list type filter (e.g. All, Service wide). */
    userTypeOptions?: SelectFilterOption[]
    /**
     * When set (e.g. from URL query params), selects matching CME / TelSys / partition and optional
     * include-children / search after option lists load. Does not replace manual filter changes until
     * option lists refresh.
     */
    urlBootstrap?: OnlyNullable<AdminListUrlBootstrap>
    /**
     * Persistent tooltip hints for Telephony Server / Partition while the value is a non-restricting
     * placeholder. Use {@code true} to show the generic i18n message, or {@code { show, title }} for a
     * custom title while {@code show} is true.
     */
    selectFilterPlaceholderTooltips?: Partial<
        Record<SelectFilterNames.TELEPHONY_SERVER | SelectFilterNames.PARTITION, SelectFilterPlaceholderTooltipEntry>
    >
    /**
     * When the URL bootstrap omits {@link AdminListUrlBootstrap.includeCmeChildren}, seed the include-child-CME
     * switch from this value. Defaults to {@code true} for existing partition/device-style lists; use {@code false}
     * for the CM Environment list where the switch means "show child CMEs of the selected parent".
     */
    includeCmeChildrenUrlFallback?: boolean
    /**
     * Optional placeholder for the search text field when {@link OptionsToDisplay.search} is enabled.
     */
    searchPlaceholder?: string
    /**
     * When true, the synthetic {@link ALL_VALUE} option for Telephony Server and Partition uses the localised
     * "None" label instead of "All". Enable only on pages where narrowing those filters is <em>required</em> for
     * meaningful results (for example lists that set {@link selectFilterPlaceholderTooltips} or flows that stay
     * idle until a concrete scope is chosen). Leave {@code false} when {@code ALL_VALUE} is a valid optional
     * aggregate (all servers / all partitions) so the sentinel stays labelled "All".
     */
    labelTelSysPartitionAllSentinelAsNone?: boolean
    /**
     * When provided, the Telephony Server dropdown only lists telsys that have at least one partition at the
     * given licence level(s). Pass e.g. {@code [ReportingAccessLevels.ENTERPRISE]} to restrict to enterprise
     * telsys, matching the behaviour used by the Users by Extension modal.
     */
    telSysLicenceFilter?: readonly string[]
}

export const ADMIN_FILTERS_SESSION_STORAGE_KEY: string = 'adminFilters.selectedScope'

/**
 * Safely reads persisted admin scope filters from session storage.
 *
 * @returns Parsed values when available, otherwise an empty object.
 */
const readAdminFiltersSessionState = (): AdminFiltersSessionState => {
    try {
        const rawState: Nullable<string> = window.sessionStorage.getItem(ADMIN_FILTERS_SESSION_STORAGE_KEY)

        if (!isStringNotEmpty(rawState ?? EMPTY_STRING)) {
            return {}
        }

        const parsedState: unknown = JSON.parse(rawState as string)

        if (IsVarEmpty(parsedState) || typeof parsedState !== 'object') {
            return {}
        }

        return parsedState as AdminFiltersSessionState
    } catch {
        return {}
    }
}

/**
 * Safely persists selected admin scope filters to session storage.
 *
 * @param patch - Partial state patch to merge with existing session values.
 */
const writeAdminFiltersSessionState = (patch: Partial<AdminFiltersSessionState>): void => {
    try {
        const currentState: AdminFiltersSessionState = readAdminFiltersSessionState()
        const mergedState: AdminFiltersSessionState = {
            ...currentState,
            ...patch
        }

        window.sessionStorage.setItem(ADMIN_FILTERS_SESSION_STORAGE_KEY, JSON.stringify(mergedState))
    } catch {
        // Ignore storage failures (private mode, quota, unavailable storage).
    }
}

/**
 * True when Telephony Server or Partition filter value is unset or a non-restricting placeholder (All / Any / -1).
 *
 * @param option - Current select value from admin filters.
 * @returns Whether the value should be treated as no concrete TelSys or partition selection.
 */
const isTelSysOrPartitionPlaceholderFilterValue = (option: Nullable<SelectFilterOption>): boolean => {
    if (IsVarEmpty(option)) {
        return true
    }

    const raw: string | number = option.value

    if (raw === ALL_VALUE) {
        return true
    }

    if (raw === ANY_VALUE_STRING_NUMERIC) {
        return true
    }

    return false
}

/**
 * Builds {@link SelectFilter} persistent tooltip props when placeholder tooltips are enabled for TelSys
 * or partition filters.
 *
 * @param entry - {@code true} for the default i18n title, or an object with {@code show} and optional title.
 * @param option - Current filter value.
 * @param defaultTitle - Title used when {@code entry} is {@code true} or when an object title is empty.
 */
const resolvePersistentTelPartTooltip = (
    entry: Definable<SelectFilterPlaceholderTooltipEntry>,
    option: Nullable<SelectFilterOption>,
    defaultTitle: string
): Definable<{
    show: boolean
    title: string
}> => {
    if (entry === true) {
        return {
            show: isTelSysOrPartitionPlaceholderFilterValue(option),
            title: defaultTitle
        }
    }

    if (isVarNotEmpty(entry) && typeof entry === 'object' && entry.show) {
        return {
            show: isTelSysOrPartitionPlaceholderFilterValue(option),
            title: isStringNotEmpty(entry.title) ? entry.title : defaultTitle
        }
    }

    return undefined
}

/**
 * True once the currently displayed admin filters have resolved enough state to safely deliver their
 * first debounced change to list pages.
 *
 * @param filterValues - Current filter values being hydrated.
 * @param optionsToDisplay - Filters enabled for the current list page.
 * @param telephonyServerOpts - Current Telephony Server options.
 * @param partitionOpts - Current Partition options.
 * @param telServersFetchPending - Whether the Telephony Server option request is still in flight.
 * @returns Whether the initial AdminFilters hydration is stable.
 */
const hasResolvedInitialDisplayedFilters = (
    filterValues: AdminFilterValues,
    optionsToDisplay: OptionsToDisplay,
    telephonyServerOpts: SelectFilterOption[],
    partitionOpts: SelectFilterOption[],
    telServersFetchPending: boolean
): boolean => {
    if (optionsToDisplay.cme === true && IsVarEmpty(filterValues.cme)) {
        return false
    }

    if (optionsToDisplay.telephonyServer === true) {
        if (telServersFetchPending) {
            return false
        }

        if (filterValues.noTelephonyServer !== true && IsVarEmpty(filterValues.telephonyServer)) {
            return false
        }

        const telephonyServerValue: Definable<string | number> = filterValues.telephonyServer?.value

        if (
            filterValues.noTelephonyServer !== true &&
            isVarNotEmpty(telephonyServerValue) &&
            telephonyServerOpts.length > 0 &&
            !telephonyServerOpts.some(
                (option: SelectFilterOption): boolean => String(option.value) === String(telephonyServerValue)
            )
        ) {
            return false
        }
    }

    if (optionsToDisplay.partition === true && filterValues.noTelephonyServer !== true) {
        if (IsVarEmpty(filterValues.telephonyServer)) {
            return false
        }

        if (IsVarEmpty(filterValues.partition)) {
            return false
        }

        const partitionValue: Definable<string | number> = filterValues.partition?.value

        if (
            isVarNotEmpty(partitionValue) &&
            partitionOpts.length > 0 &&
            !partitionOpts.some(
                (option: SelectFilterOption): boolean => String(option.value) === String(partitionValue)
            )
        ) {
            return false
        }
    }

    return true
}

export const AdminFilters: FC<AdminFiltersProps> = ({
    onChange,
    localeText,
    optionsToDisplay,
    onRefresh,
    cmeFiltering: [cmeFields, cmeFiltering] = [[], (): boolean => true],
    alignGrid = true,
    additionalFilters,
    additionalFilterSkeletonCount = 0,
    scopeOptions = [],
    codeTypeOptions = [],
    userTypeOptions = [],
    urlBootstrap = null,
    selectFilterPlaceholderTooltips = {},
    includeCmeChildrenUrlFallback = true,
    labelTelSysPartitionAllSentinelAsNone = false,
    searchPlaceholder,
    telSysLicenceFilter = [],
    labelAlign = 'top',
    allowCXIOnly = true
}: AdminFiltersProps): ReactElement => {
    const sessionBootstrapRef: MutableRefObject<AdminFiltersSessionState> = useRef<AdminFiltersSessionState>(
        readAdminFiltersSessionState()
    )
    const hasUrlBootstrapCme: boolean = isStringNotEmpty(urlBootstrap?.cmeId ?? EMPTY_STRING)
    const hasUrlBootstrapTelSys: boolean = isStringNotEmpty(urlBootstrap?.telephonyServer ?? EMPTY_STRING)
    const hasUrlBootstrapPartition: boolean = isStringNotEmpty(urlBootstrap?.partition ?? EMPTY_STRING)
    const preferredCmeBootstrap: Definable<string> = hasUrlBootstrapCme
        ? urlBootstrap?.cmeId
        : sessionBootstrapRef.current.cmeId
    const preferredTelSysBootstrap: Definable<string> = hasUrlBootstrapTelSys
        ? urlBootstrap?.telephonyServer
        : sessionBootstrapRef.current.telephonyServer
    const preferredPartitionBootstrap: Definable<string> = hasUrlBootstrapPartition
        ? urlBootstrap?.partition
        : sessionBootstrapRef.current.partition

    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(GenericAdminFilterLanguage.NAME)

    const allOptionLabel: string = language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_OPTION_ALL_LABEL)
    const noneOptionLabel: string = language.get(
        GenericAdminFilterLanguage.GenericAdminFilterLanguage_OPTION_NONE_LABEL
    )
    const telPartAllSentinelLabel: string = labelTelSysPartitionAllSentinelAsNone ? noneOptionLabel : allOptionLabel
    const anyOptionLabel: string = language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_OPTION_ANY_LABEL)
    const selectPlaceholderTooltipTitle: string = language.get(
        GenericAdminFilterLanguage.GenericAdminFilterLanguage_PLEASE_SELECT_OPTION_TO_SHOW_RESULTS
    )

    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(true)
    const [isHydratingInitialFilters, setIsHydratingInitialFilters]: ReactState<boolean> = useState<boolean>(true)
    /**
     * Mirrors {@link isHydratingInitialFilters} for guards inside effects that must not list that flag in their
     * dependency arrays; otherwise when hydration completes those effects re-run and emit a duplicate debounced
     * {@code onChange}, which makes admin lists fetch twice (defaults, then session/URL scope).
     */
    const isHydratingInitialFiltersRef: MutableRefObject<boolean> = useRef<boolean>(true)

    useEffect((): void => {
        isHydratingInitialFiltersRef.current = isHydratingInitialFilters
    }, [isHydratingInitialFilters])
    const [cmesOpts, setCmesOpts]: ReactState<SelectFilterOption[]> = useState<SelectFilterOption[]>([])
    const [telephonyServerOpts, setTelephonyServerOpts]: ReactState<SelectFilterOption[]> = useState<
        SelectFilterOption[]
    >([])
    const [partitionOpts, setPartitionOpts]: ReactState<SelectFilterOption[]> = useState<SelectFilterOption[]>([])
    const [filterValues, setFilterValues]: ReactState<AdminFilterValues> = useState<AdminFilterValues>({
        cme: null,
        telephonyServer: null,
        partition: null,
        crmType: { value: ANY_VALUE_STRING_NUMERIC, label: anyOptionLabel },
        licenceType: null,
        scope: null,
        codeType: null,
        userType: null,
        includeCmeChildren:
            urlBootstrap?.includeCmeChildren !== undefined
                ? urlBootstrap.includeCmeChildren
                : includeCmeChildrenUrlFallback,
        search: urlBootstrap?.search ?? EMPTY_STRING,
        noTelephonyServer: false,
        changeLogBefore: EMPTY_STRING,
        changeLogActions: [],
        changeLogTypes: [],
        changeLogSuppressNulls: false,
        cxiTelSysOnly: true
    })

    const [allCrmTypeOptions]: CRMTypes = useCRMTypes()
    const dedupedCrmTypeOptions: CRMTypesResponse[] = isVarNotEmpty(allCrmTypeOptions)
        ? [
              ...new Map(
                  Object.values(allCrmTypeOptions)
                      .flat()
                      .map((item: CRMTypesResponse): [string, CRMTypesResponse] => [item.crmTypeName, item])
              ).values()
          ]
        : []

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    /**
     * True while {@link getTelServersAsSelectOptions} is in flight for the current CME. Used so URL
     * telephony bootstrap does not match against stale options from a previous CME or emit debounced
     * onChange before the list for the requested scope (e.g. include child CMEs) has loaded.
     */
    const telServersFetchPendingRef: MutableRefObject<boolean> = useRef<boolean>(false)

    const filterDeliverySubject: MutableRefObject<Subject<AdminFilterValues>> = useRef(new Subject<AdminFilterValues>())

    const [crmTypeOpts]: CRMTypes = useCRMTypes()

    const [telServersFetchEpoch, setTelServersFetchEpoch]: ReactState<number> = useState<number>(0)

    const mappedCrmTypeOpts: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            { value: ANY_VALUE_STRING_NUMERIC, label: anyOptionLabel },
            ...(dedupedCrmTypeOptions
                ?.map(
                    ({ crmType, crmTypeName }: CRMTypesResponse): SelectFilterOption => ({
                        label: crmTypeName,
                        value: crmType
                    })
                )
                .sort((a: SelectFilterOption, b: SelectFilterOption): number =>
                    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
                ) ?? [])
        ],
        [crmTypeOpts, anyOptionLabel]
    )

    const [userInfo]: UserInfo = useUserInfo()

    const getCMEs = (): void => {
        setIsLoading(true)

        getCMEsAsSelectOptions(cmeFields, cmeFiltering)
            .then((cmes: SelectOptions<string>): void => {
                if (isMounted.current) {
                    setCmesOpts(
                        cmes.map(
                            ({ label, value }: SelectOption<string>): SelectFilterOption => ({
                                label,
                                value
                            })
                        )
                    )
                }
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) {
                    setIsLoading(false)
                }
            })
    }

    const getTelServers = (cmeID: string): void => {
        telServersFetchPendingRef.current = true
        setIsLoading(true)
        getTelServersAsSelectOptionsWithCXIFilter(
            cmeID,
            filterValues.includeCmeChildren ?? false,
            allowCXIOnly ? filterValues.cxiTelSysOnly : false,
            false,
            ...telSysLicenceFilter
        )
            .then((telSyss: SelectOptions<number>): void => {
                if (isMounted.current) {
                    setTelephonyServerOpts([
                        ...(PermissionsUtil.isTelSysWideUser(userInfo)
                            ? [{ label: telPartAllSentinelLabel, value: ALL_VALUE }]
                            : []),
                        ...telSyss.map(
                            ({ label, value, telSysType }: SelectOption<number>): SelectFilterOption => ({
                                label,
                                value,
                                telSysType
                            })
                        )
                    ])
                }
            })
            .catch((e: Error): void => {
                $error(e)

                if (isMounted.current) {
                    setTelephonyServerOpts([{ label: telPartAllSentinelLabel, value: ALL_VALUE }])
                }
            })
            .finally((): void => {
                telServersFetchPendingRef.current = false

                if (isMounted.current) {
                    setIsLoading(false)
                    setTelServersFetchEpoch((current: number): number => current + 1)
                }
            })
    }

    const getPartitions = (telSys: number): void => {
        setIsLoading(true)
        getPartitionsForTelServer(telSys)
            .then((partitions: SelectOptions<number>): void => {
                if (isMounted.current) {
                    setPartitionOpts([
                        ...(PermissionsUtil.isPartitionWideUser(userInfo)
                            ? [{ label: telPartAllSentinelLabel, value: ALL_VALUE }]
                            : []),
                        ...partitions.map(
                            ({ label, value }: SelectOption<number>): SelectFilterOption => ({
                                label,
                                value
                            })
                        )
                    ])
                }
            })
            .catch((e: Error): void => {
                $error(e)

                if (isMounted.current) {
                    setPartitionOpts([{ label: telPartAllSentinelLabel, value: ALL_VALUE }])
                }
            })
            .finally((): void => {
                if (isMounted.current) {
                    setIsLoading(false)
                }
            })
    }

    useEffect((): void => {
        setTelephonyServerOpts((prev: SelectFilterOption[]): SelectFilterOption[] =>
            prev.map(
                (opt: SelectFilterOption): SelectFilterOption =>
                    opt.value === ALL_VALUE ? { ...opt, label: telPartAllSentinelLabel } : opt
            )
        )
        setPartitionOpts((prev: SelectFilterOption[]): SelectFilterOption[] =>
            prev.map(
                (opt: SelectFilterOption): SelectFilterOption =>
                    opt.value === ALL_VALUE ? { ...opt, label: telPartAllSentinelLabel } : opt
            )
        )
    }, [telPartAllSentinelLabel])

    useEffect((): void => {
        setFilterValues((prev: AdminFilterValues): AdminFilterValues => {
            const telUnchanged: boolean =
                prev.telephonyServer?.value !== ALL_VALUE || prev.telephonyServer?.label === telPartAllSentinelLabel
            const partUnchanged: boolean =
                prev.partition?.value !== ALL_VALUE || prev.partition?.label === telPartAllSentinelLabel

            if (telUnchanged && partUnchanged) {
                return prev
            }

            return {
                ...prev,
                ...(prev.telephonyServer?.value === ALL_VALUE
                    ? { telephonyServer: { value: ALL_VALUE, label: telPartAllSentinelLabel } }
                    : {}),
                ...(prev.partition?.value === ALL_VALUE
                    ? { partition: { value: ALL_VALUE, label: telPartAllSentinelLabel } }
                    : {})
            }
        })
    }, [telPartAllSentinelLabel])

    useEffect((): void => {
        if (!cmesOpts?.length) {
            return
        }

        const bootstrapCmeId: Definable<string> = preferredCmeBootstrap
        const matchedCme: Nullable<SelectFilterOption> =
            isStringNotEmpty(bootstrapCmeId ?? EMPTY_STRING) && cmesOpts.length > 0
                ? cmesOpts.find((opt: SelectFilterOption): boolean => String(opt.value) === String(bootstrapCmeId)) ??
                  null
                : null

        setFilterValues((currentFilterValues: AdminFilterValues): AdminFilterValues => {
            const newFilterValues: AdminFilterValues = {
                ...currentFilterValues,
                cme: matchedCme ?? cmesOpts[0]
            }

            if (!isHydratingInitialFiltersRef.current) {
                filterDeliverySubject.current.next(newFilterValues)
            }

            return newFilterValues
        })
    }, [JSON.stringify(cmesOpts), preferredCmeBootstrap])

    useEffect((): void => {
        if (isStringNotEmpty(filterValues?.cme?.value as string)) {
            setIsLoading(true)
            setFilterValues(
                (currentFilterValues: AdminFilterValues): AdminFilterValues => ({
                    ...currentFilterValues,
                    telephonyServer: telephonyServerOpts?.[0],
                    partition: partitionOpts?.[0]
                })
            )
            getTelServers(filterValues?.cme?.value as string)
        }
    }, [filterValues?.cme?.value, filterValues?.includeCmeChildren])

    useEffect((): void => {
        const telOptsForBootstrap: SelectFilterOption[] = telServersFetchPendingRef.current ? [] : telephonyServerOpts

        const filteredTelSysOpts: SelectFilterOption[] = telOptsForBootstrap.filter(
            (option: SelectFilterOption): boolean => option.value !== ALL_VALUE
        )

        const hasAllTelSysScopePlaceholder: boolean = telOptsForBootstrap.some(
            (option: SelectFilterOption): boolean => option.value === ALL_VALUE
        )

        const hasNoTelSysOption: boolean = filteredTelSysOpts.length === 0 && !hasAllTelSysScopePlaceholder

        const suppressDebouncedOnChangeWhileTelListLoading: boolean =
            Boolean(optionsToDisplay.telephonyServer) && telServersFetchPendingRef.current

        setFilterValues((currentFilterValues: AdminFilterValues): AdminFilterValues => {
            const bootstrapTel: Definable<string> = preferredTelSysBootstrap
            const matchedTel: Nullable<SelectFilterOption> =
                isStringNotEmpty(bootstrapTel ?? EMPTY_STRING) && telOptsForBootstrap.length > 0
                    ? telOptsForBootstrap.find(
                          (opt: SelectFilterOption): boolean => String(opt.value) === String(bootstrapTel)
                      ) ?? null
                    : null

            // When session/URL does not pin a TelSys, prefer the first concrete server over the synthetic "All"
            // row so REST lists match legacy servlet pages (empty tbTelSysID uses the user's session telephone
            // system, not the all-servers sentinel).
            const defaultTelSysWhenUnset: Nullable<SelectFilterOption> =
                filteredTelSysOpts.length > 0 ? filteredTelSysOpts[0] ?? null : null

            const newFilterValues: AdminFilterValues = {
                ...currentFilterValues,
                telephonyServer: matchedTel ?? defaultTelSysWhenUnset ?? telOptsForBootstrap?.[0] ?? null,
                noTelephonyServer: hasNoTelSysOption
            }

            if (!isHydratingInitialFiltersRef.current && !suppressDebouncedOnChangeWhileTelListLoading) {
                filterDeliverySubject.current.next(newFilterValues)
            }

            return newFilterValues
        })
    }, [
        JSON.stringify(telephonyServerOpts),
        filterValues?.includeCmeChildren,
        preferredTelSysBootstrap,
        optionsToDisplay.telephonyServer,
        telServersFetchEpoch
    ])

    useEffect((): void => {
        const newFilterValues: AdminFilterValues = {
            ...filterValues,
            partition: partitionOpts?.[0]
        }

        setFilterValues(newFilterValues)
        filterDeliverySubject.current.next(newFilterValues)
    }, [JSON.stringify(partitionOpts)])

    useEffect((): void => {
        const telephonyServer: Nullable<SelectFilterOption> = filterValues.telephonyServer
        const telephonyServerValue: Definable<string | number> = telephonyServer?.value

        if (!isVarNotEmpty(telephonyServerValue)) {
            return
        }

        setIsLoading(true)

        setFilterValues(
            (currentFilterValues: AdminFilterValues): AdminFilterValues => ({
                ...currentFilterValues,
                partition: partitionOpts?.[0]
            })
        )

        if (telephonyServerValue === ALL_VALUE) {
            setPartitionOpts([{ label: telPartAllSentinelLabel, value: ALL_VALUE }])
            setIsLoading(false)
        } else {
            getPartitions(telephonyServerValue as number)
        }
    }, [telPartAllSentinelLabel, filterValues?.telephonyServer?.value])

    useEffect((): void => {
        const bootstrapPart: Definable<string> = preferredPartitionBootstrap
        const matchedPart: Nullable<SelectFilterOption> =
            isStringNotEmpty(bootstrapPart ?? EMPTY_STRING) && (partitionOpts?.length ?? 0) > 0
                ? partitionOpts.find(
                      (opt: SelectFilterOption): boolean => String(opt.value) === String(bootstrapPart)
                  ) ?? null
                : null

        setFilterValues((currentFilterValues: AdminFilterValues): AdminFilterValues => {
            const newFilterValues: AdminFilterValues = {
                ...currentFilterValues,
                partition: matchedPart ?? partitionOpts?.[0] ?? null
            }

            if (!isHydratingInitialFiltersRef.current) {
                filterDeliverySubject.current.next(newFilterValues)
            }

            return newFilterValues
        })
    }, [JSON.stringify(partitionOpts), preferredPartitionBootstrap])

    useEffect(getCMEs, [filterValues?.includeCmeChildren])

    useEffect((): void => {
        if (!optionsToDisplay?.userType || userTypeOptions.length === 0) {
            return
        }

        setFilterValues((currentFilterValues: AdminFilterValues): AdminFilterValues => {
            if (isVarNotEmpty(currentFilterValues.userType)) {
                return currentFilterValues
            }

            return {
                ...currentFilterValues,
                userType: userTypeOptions[0]
            }
        })
    }, [optionsToDisplay?.userType, JSON.stringify(userTypeOptions)])

    /**
     * Synthetic Telephony / Partition "All" (or "None" when {@link labelTelSysPartitionAllSentinelAsNone}) row.
     */
    const telPartitionAllSentinelOption = (): SelectFilterOption => ({
        label: telPartAllSentinelLabel,
        value: ALL_VALUE
    })

    const handleSelectFilterChange: (filter: SelectFilterNames, option: Nullable<SelectFilterOption>) => void =
        useCallback(
            (filter: SelectFilterNames, option: Nullable<SelectFilterOption>): void => {
                const allTelOrPartitionRow: SelectFilterOption = telPartitionAllSentinelOption()

                if (filter === SelectFilterNames.CME && isVarNotEmpty(option?.value)) {
                    writeAdminFiltersSessionState({
                        cmeId: String(option?.value),
                        telephonyServer: String(ALL_VALUE),
                        partition: String(ALL_VALUE)
                    })
                }

                if (filter === SelectFilterNames.TELEPHONY_SERVER && isVarNotEmpty(option?.value)) {
                    writeAdminFiltersSessionState({
                        telephonyServer: String(option?.value),
                        partition: String(ALL_VALUE)
                    })
                }

                if (filter === SelectFilterNames.PARTITION && isVarNotEmpty(option?.value)) {
                    writeAdminFiltersSessionState({ partition: String(option?.value) })
                }

                let newFilterValues: AdminFilterValues = { ...filterValues, [filter]: option }

                if (filter === SelectFilterNames.CME) {
                    newFilterValues = {
                        ...newFilterValues,
                        telephonyServer: PermissionsUtil.isTelSysWideUser(userInfo) ? allTelOrPartitionRow : null,
                        partition: PermissionsUtil.isPartitionWideUser(userInfo) ? allTelOrPartitionRow : null,
                        noTelephonyServer: false,
                        ...(optionsToDisplay.crmType
                            ? { crmType: { value: ANY_VALUE_STRING_NUMERIC, label: anyOptionLabel } }
                            : {}),
                        ...(optionsToDisplay.scope && scopeOptions.length > 0
                            ? { scope: scopeOptions[0] ?? null }
                            : {}),
                        ...(optionsToDisplay.codeType && codeTypeOptions.length > 0
                            ? { codeType: codeTypeOptions[0] ?? null }
                            : {}),
                        ...(optionsToDisplay.userType && userTypeOptions.length > 0
                            ? { userType: userTypeOptions[0] ?? null }
                            : {})
                    }
                } else if (filter === SelectFilterNames.TELEPHONY_SERVER) {
                    newFilterValues = {
                        ...newFilterValues,
                        partition: PermissionsUtil.isPartitionWideUser(userInfo) ? allTelOrPartitionRow : null
                    }
                } else if (filter === SelectFilterNames.CRM_TYPE) {
                    newFilterValues = {
                        ...newFilterValues,
                        ...(optionsToDisplay.scope && scopeOptions.length > 0
                            ? { scope: scopeOptions[0] ?? null }
                            : {}),
                        ...(optionsToDisplay.codeType && codeTypeOptions.length > 0
                            ? { codeType: codeTypeOptions[0] ?? null }
                            : {}),
                        ...(optionsToDisplay.userType && userTypeOptions.length > 0
                            ? { userType: userTypeOptions[0] ?? null }
                            : {})
                    }
                } else if (filter === SelectFilterNames.SCOPE) {
                    newFilterValues = {
                        ...newFilterValues,
                        ...(optionsToDisplay.codeType && codeTypeOptions.length > 0
                            ? { codeType: codeTypeOptions[0] ?? null }
                            : {}),
                        ...(optionsToDisplay.userType && userTypeOptions.length > 0
                            ? { userType: userTypeOptions[0] ?? null }
                            : {})
                    }
                } else if (filter === SelectFilterNames.CODE_TYPE) {
                    newFilterValues = {
                        ...newFilterValues,
                        ...(optionsToDisplay.userType && userTypeOptions.length > 0
                            ? { userType: userTypeOptions[0] ?? null }
                            : {})
                    }
                }

                setFilterValues(newFilterValues)

                if (!isHydratingInitialFiltersRef.current) {
                    filterDeliverySubject.current.next(newFilterValues)
                }
            },
            [
                filterValues,
                userInfo,
                telPartAllSentinelLabel,
                anyOptionLabel,
                optionsToDisplay.crmType,
                optionsToDisplay.scope,
                optionsToDisplay.codeType,
                optionsToDisplay.userType,
                scopeOptions,
                codeTypeOptions,
                userTypeOptions
            ]
        )

    useEffect((): void => {
        if (!hasUrlBootstrapCme) {
            return
        }

        writeAdminFiltersSessionState({ cmeId: String(urlBootstrap?.cmeId) })
    }, [hasUrlBootstrapCme, urlBootstrap?.cmeId])

    useEffect((): void => {
        if (!hasUrlBootstrapTelSys) {
            return
        }

        writeAdminFiltersSessionState({ telephonyServer: String(urlBootstrap?.telephonyServer) })
    }, [hasUrlBootstrapTelSys, urlBootstrap?.telephonyServer])

    useEffect((): void => {
        if (!hasUrlBootstrapPartition) {
            return
        }

        writeAdminFiltersSessionState({ partition: String(urlBootstrap?.partition) })
    }, [hasUrlBootstrapPartition, urlBootstrap?.partition])

    const handleSwitchFilterChange: (filter: SwitchFilterNames, value: Nullable<boolean>) => void = useCallback(
        (filter: SwitchFilterNames, value: Nullable<boolean>): void => {
            const allTelOrPartitionRow: SelectFilterOption = telPartitionAllSentinelOption()

            let newFilterValues: AdminFilterValues = { ...filterValues, [filter]: value }

            if (filter === SwitchFilterNames.INCLUDE_CME_CHILDREN) {
                writeAdminFiltersSessionState({
                    telephonyServer: String(ALL_VALUE),
                    partition: String(ALL_VALUE)
                })

                newFilterValues = {
                    ...newFilterValues,
                    telephonyServer: PermissionsUtil.isTelSysWideUser(userInfo) ? allTelOrPartitionRow : null,
                    partition: PermissionsUtil.isPartitionWideUser(userInfo) ? allTelOrPartitionRow : null,
                    noTelephonyServer: false
                }
            }

            setFilterValues(newFilterValues)

            if (!isHydratingInitialFiltersRef.current) {
                filterDeliverySubject.current.next(newFilterValues)
            }
        },
        [filterValues, userInfo, telPartAllSentinelLabel]
    )

    const handleTextFilterChange: (filter: TextFilterNames, value: string) => void = useCallback(
        (filter: TextFilterNames, value: string): void => {
            const newFilterValues: AdminFilterValues = { ...filterValues, [filter]: value }

            setFilterValues(newFilterValues)
            if (!isHydratingInitialFiltersRef.current) {
                filterDeliverySubject.current.next(newFilterValues)
            }
        },
        [filterValues]
    )

    const handleChangeLogActionsChange = useCallback(
        (nextActions: readonly string[]): void => {
            const newFilterValues: AdminFilterValues = { ...filterValues, changeLogActions: [...nextActions] }

            setFilterValues(newFilterValues)

            if (!isHydratingInitialFiltersRef.current) {
                filterDeliverySubject.current.next(newFilterValues)
            }
        },
        [filterValues]
    )

    const handleChangeLogTypesChange = useCallback(
        (nextTypes: readonly string[]): void => {
            const newFilterValues: AdminFilterValues = { ...filterValues, changeLogTypes: [...nextTypes] }

            setFilterValues(newFilterValues)

            if (!isHydratingInitialFiltersRef.current) {
                filterDeliverySubject.current.next(newFilterValues)
            }
        },
        [filterValues]
    )

    const handleChangeLogSuppressNullsChange = useCallback(
        (suppressNulls: boolean): void => {
            const newFilterValues: AdminFilterValues = { ...filterValues, changeLogSuppressNulls: suppressNulls }

            setFilterValues(newFilterValues)

            if (!isHydratingInitialFiltersRef.current) {
                filterDeliverySubject.current.next(newFilterValues)
            }
        },
        [filterValues]
    )

    useEffect((): void => {
        const hasInclude: boolean = urlBootstrap?.includeCmeChildren !== undefined
        const hasSearch: boolean = urlBootstrap?.search !== undefined

        if (!hasInclude && !hasSearch) {
            return
        }

        setFilterValues((currentFilterValues: AdminFilterValues): AdminFilterValues => {
            const newFilterValues: AdminFilterValues = {
                ...currentFilterValues,
                ...(hasInclude ? { includeCmeChildren: urlBootstrap?.includeCmeChildren } : {}),
                ...(hasSearch ? { search: urlBootstrap?.search ?? EMPTY_STRING } : {})
            }

            if (!isHydratingInitialFiltersRef.current) {
                filterDeliverySubject.current.next(newFilterValues)
            }

            return newFilterValues
        })
    }, [urlBootstrap?.includeCmeChildren, urlBootstrap?.search])

    useEffect((): void => {
        if (!isHydratingInitialFilters) {
            return
        }

        if (
            !hasResolvedInitialDisplayedFilters(
                filterValues,
                optionsToDisplay,
                telephonyServerOpts,
                partitionOpts,
                telServersFetchPendingRef.current
            )
        ) {
            return
        }

        setIsHydratingInitialFilters(false)
        filterDeliverySubject.current.next(filterValues)
    }, [
        filterValues,
        optionsToDisplay,
        partitionOpts,
        telephonyServerOpts,
        isHydratingInitialFilters,
        telServersFetchEpoch
    ])

    useEffect((): (() => void) => {
        const subscription: Subscription = filterDeliverySubject.current
            .pipe(debounceTime(400))
            .subscribe((value: AdminFilterValues): void => {
                onChange(value)
            })

        return (): void => {
            subscription.unsubscribe()
        }
    }, [onChange])

    const skeletonExtras: ReactElement[] = Array.from(
        { length: additionalFilterSkeletonCount },
        (_: unknown, index: number) => <Skeleton key={`af-extra-skel-${String(index)}`} />
    )

    return (
        <div className='router-admin-filters'>
            {isLoading || isHydratingInitialFilters ? (
                <div className={clsx('router-admin-filters-loader', alignGrid ? 'router-admin-filters-grid' : '')}>
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                    {skeletonExtras}
                    {optionsToDisplay?.includeRefresh || optionsToDisplay?.search ? (
                        <>
                            <Skeleton />
                            <div className='af-right-side'>
                                <Skeleton />
                            </div>
                        </>
                    ) : (
                        <></>
                    )}
                </div>
            ) : (
                <div className={alignGrid ? 'router-admin-filters-grid' : ''}>
                    {optionsToDisplay?.cme && cmesOpts?.length > 0 ? (
                        <div>
                            <SelectFilter
                                onChange={(value: Nullable<SelectFilterOption>): void => {
                                    handleSelectFilterChange(SelectFilterNames.CME, value)
                                }}
                                options={cmesOpts}
                                label={localeText.cmeLabel}
                                icon={<CmeIcon />}
                                disabled={isLoading}
                                value={filterValues.cme}
                                defaultValue={DEFAULT_CME_ID}
                                alignLabel={labelAlign}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                    {optionsToDisplay?.telephonyServer && telephonyServerOpts?.length > 0 ? (
                        <div>
                            <SelectFilter
                                onChange={(value: Nullable<SelectFilterOption>): void => {
                                    handleSelectFilterChange(SelectFilterNames.TELEPHONY_SERVER, value)
                                }}
                                options={telephonyServerOpts}
                                label={localeText.telephonyServerLabel ?? EMPTY_STRING}
                                icon={<TelSysIcon />}
                                disabled={isLoading}
                                value={filterValues.telephonyServer}
                                defaultValue={ALL_VALUE}
                                persistentPlaceholderTooltip={resolvePersistentTelPartTooltip(
                                    selectFilterPlaceholderTooltips[SelectFilterNames.TELEPHONY_SERVER],
                                    filterValues.telephonyServer,
                                    selectPlaceholderTooltipTitle
                                )}
                                alignLabel={labelAlign}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                    {optionsToDisplay?.partition ? (
                        <div>
                            <SelectFilter
                                onChange={(value: Nullable<SelectFilterOption>): void => {
                                    handleSelectFilterChange(SelectFilterNames.PARTITION, value)
                                }}
                                options={partitionOpts}
                                label={localeText.partitionLabel ?? EMPTY_STRING}
                                icon={<PartitionIcon />}
                                disabled={isLoading}
                                value={filterValues.partition}
                                defaultValue={ALL_VALUE}
                                persistentPlaceholderTooltip={resolvePersistentTelPartTooltip(
                                    selectFilterPlaceholderTooltips[SelectFilterNames.PARTITION],
                                    filterValues.partition,
                                    selectPlaceholderTooltipTitle
                                )}
                                alignLabel={labelAlign}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                    {optionsToDisplay?.crmType ? (
                        <div>
                            <SelectFilter
                                onChange={(value: Nullable<SelectFilterOption>): void => {
                                    handleSelectFilterChange(SelectFilterNames.CRM_TYPE, value)
                                }}
                                options={mappedCrmTypeOpts}
                                label={localeText.crmTypeLabel ?? EMPTY_STRING}
                                icon={<CrmIcon />}
                                disabled={isLoading}
                                value={filterValues.crmType}
                                defaultValue={ANY_VALUE_STRING_NUMERIC}
                                alignLabel={labelAlign}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                    {optionsToDisplay?.scope && scopeOptions.length > 0 ? (
                        <div>
                            <SelectFilter
                                onChange={(value: Nullable<SelectFilterOption>): void => {
                                    handleSelectFilterChange(SelectFilterNames.SCOPE, value)
                                }}
                                options={scopeOptions}
                                label={localeText.scopeLabel ?? EMPTY_STRING}
                                icon={<FilterListIcon />}
                                disabled={isLoading}
                                value={filterValues.scope}
                                defaultValue={scopeOptions[0]?.value}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                    {optionsToDisplay?.codeType && codeTypeOptions.length > 0 ? (
                        <div>
                            <SelectFilter
                                onChange={(value: Nullable<SelectFilterOption>): void => {
                                    handleSelectFilterChange(SelectFilterNames.CODE_TYPE, value)
                                }}
                                options={codeTypeOptions}
                                label={localeText.codeTypeLabel ?? EMPTY_STRING}
                                icon={<CategoryIcon />}
                                disabled={isLoading}
                                value={filterValues.codeType}
                                defaultValue={codeTypeOptions[0]?.value}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                    {optionsToDisplay?.userType && userTypeOptions.length > 0 ? (
                        <div>
                            <SelectFilter
                                onChange={(value: Nullable<SelectFilterOption>): void => {
                                    handleSelectFilterChange(SelectFilterNames.USER_TYPE, value)
                                }}
                                options={userTypeOptions}
                                label={localeText.userTypeLabel ?? EMPTY_STRING}
                                icon={<FilterListIcon />}
                                disabled={isLoading}
                                value={filterValues.userType}
                                defaultValue={userTypeOptions[0]?.value}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                    {additionalFilters ? (
                        <>
                            {additionalFilters({
                                isLoading,
                                filterValues,
                                onSelectFilterChange: handleSelectFilterChange,
                                onChangeLogActionsChange: handleChangeLogActionsChange,
                                onChangeLogTypesChange: handleChangeLogTypesChange,
                                onChangeLogSuppressNullsChange: handleChangeLogSuppressNullsChange,
                                onTextFilterChange: handleTextFilterChange
                            })}
                        </>
                    ) : (
                        <></>
                    )}
                    {optionsToDisplay?.includeCmeChildren ? (
                        <div>
                            <FormControlLabel
                                onChange={(_: ChangeEvent<object>, value: boolean): void =>
                                    handleSwitchFilterChange(SwitchFilterNames.INCLUDE_CME_CHILDREN, value)
                                }
                                disabled={isLoading}
                                control={<Switch color='primary' checked={filterValues.includeCmeChildren ?? false} />}
                                label={localeText.includeChildCmeLabel}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                    {optionsToDisplay?.includeRefresh || optionsToDisplay?.search ? (
                        <div className='af-right-side'>
                            {optionsToDisplay?.includeRefresh && !IsVarEmpty(onRefresh) ? (
                                <IconButton
                                    customClassName='refresh-button'
                                    iconName={IconName.REFRESH}
                                    type={IconButtonType.BUTTON}
                                    onClick={onRefresh}
                                />
                            ) : (
                                <></>
                            )}
                            {optionsToDisplay?.search ? (
                                <TextInput
                                    className='af-text-filter'
                                    variant='outlined'
                                    labelInput={localeText.searchLabel}
                                    placeholder={searchPlaceholder}
                                    value={filterValues.search}
                                    adornmentEnd={
                                        filterValues.search === EMPTY_STRING ? undefined : (
                                            <IconButton
                                                type={IconButtonType.BUTTON}
                                                iconName={IconName.X}
                                                onClick={(): void =>
                                                    handleTextFilterChange(TextFilterNames.SEARCH, EMPTY_STRING)
                                                }
                                            />
                                        )
                                    }
                                    onChange={(search: string): void =>
                                        handleTextFilterChange(TextFilterNames.SEARCH, search)
                                    }
                                />
                            ) : (
                                <></>
                            )}
                        </div>
                    ) : (
                        <></>
                    )}
                </div>
            )}
        </div>
    )
}
